import i18next, { type TFunction } from 'i18next';
import enTranslation from 'locales/en/translation.json';
import type { Bet } from 'types/Bet';
import type { LiveEventBet } from 'types/LiveEvent';
import type { Odd } from 'types/Odd';
import type { LiveEventOdd } from 'types/LiveEvent';
import {
  translateMarketNameByText,
  translateSelectionByText,
} from './bettingI18n';
import { marketDisplayLabel } from './marketCatalog';

export function betMarketTranslationKey(customId: number): string {
  return `betMarkets.${customId}`;
}

export function translateBetMarketName(
  bet: Bet | LiveEventBet,
  t: TFunction
): string {
  const rawName = bet.name;
  // The catalogue names the market the way players here expect it; only
  // markets it does not cover fall through to the feed's own wording.
  const catalogued = marketDisplayLabel(rawName);
  if (catalogued) return catalogued;
  let id: number | undefined;
  if ('custom_id' in bet && bet.custom_id != null && bet.custom_id !== '') {
    const n =
      typeof bet.custom_id === 'number'
        ? bet.custom_id
        : Number.parseInt(String(bet.custom_id), 10);
    id = Number.isFinite(n) ? n : undefined;
  }
  if (id != null && i18next.exists(betMarketTranslationKey(id))) {
    return t(betMarketTranslationKey(id), { defaultValue: rawName });
  }
  return translateMarketNameByText(rawName) ?? rawName;
}

const normalizeMarketName = (name: string) =>
  name.toLowerCase().replace(/[^a-z0-9]/g, '');

/** English market name (normalised) → catalogue id, built from the en locale. */
const MARKET_ID_BY_ENGLISH_NAME: Map<string, number> = (() => {
  const byName = new Map<string, number>();
  const markets = (enTranslation as { betMarkets?: Record<string, string> })
    .betMarkets;
  Object.entries(markets ?? {}).forEach(([id, name]) => {
    const key = normalizeMarketName(String(name));
    const n = Number.parseInt(id, 10);
    if (key && Number.isFinite(n) && !byName.has(key)) byName.set(key, n);
  });
  return byName;
})();

/**
 * Ticket legs keep the market as the provider's English text ("Fulltime
 * Result") with no catalogue id, so they never went through betMarkets and
 * showed in English on a German or Serbian ticket. Match the name back to its
 * catalogue entry (ignoring spacing and case) and translate it; markets the
 * catalogue does not cover keep their stored name.
 */
export function translateStoredMarketName(
  name: string | null | undefined,
  t: TFunction
): string {
  const raw = String(name ?? '').trim();
  if (!raw) return '';
  const catalogued = marketDisplayLabel(raw);
  if (catalogued) return catalogued;
  const id = MARKET_ID_BY_ENGLISH_NAME.get(normalizeMarketName(raw));
  if (id != null) {
    return t(betMarketTranslationKey(id), { defaultValue: raw });
  }
  return translateMarketNameByText(raw) ?? raw;
}

/** Resolve label for a filter row where `selectedApiName` matches `bet.name` from the API. */
export function translateBetMarketNameForSelection(
  selectedApiName: string,
  bets: Bet[],
  t: TFunction
): string {
  const bet = (bets || []).find((b) => b?.name === selectedApiName);
  return bet != null ? translateBetMarketName(bet, t) : selectedApiName;
}

const SIMPLE_ODD_KEYS: Record<string, string> = {
  Over: 'bet_odd_over',
  Under: 'bet_odd_under',
  Draw: 'bet_odd_draw',
  Yes: 'bet_odd_yes',
  No: 'bet_odd_no',
  Odd: 'bet_odd_odd',
  Even: 'bet_odd_even',
  Exactly: 'bet_odd_exactly',
  '1': 'bet_odd_home_1x2',
  '2': 'bet_odd_away_1x2',
  Winner: 'bet_odd_winner',
  Handicap: 'bet_odd_handicap',
  Total: 'bet_odd_total',
  'To Win': 'bet_odd_to_win',
  Line: 'bet_odd_line',
  'Money Line': 'bet_odd_money_line',
  Tie: 'bet_odd_tie',
  'Yes/Yes': 'bet_odd_yes_yes',
  'Yes/No': 'bet_odd_yes_no',
  'No/Yes': 'bet_odd_no_yes',
  'No/No': 'bet_odd_no_no',
};

function translateHandicapLine(raw: string, t: TFunction): string {
  const s = raw.trim();
  const over = s.match(/^O\s+([\d.]+)$/i);
  if (over) {
    return t('bet_odd_total_over_line', {
      line: over[1],
      defaultValue: s,
    });
  }
  const under = s.match(/^U\s+([\d.]+)$/i);
  if (under) {
    return t('bet_odd_total_under_line', {
      line: under[1],
      defaultValue: s,
    });
  }
  const exact = s.match(/^E\s+([\d.]+)$/i);
  if (exact) {
    return t('bet_odd_total_exact_line', {
      line: exact[1],
      defaultValue: s,
    });
  }
  // Team lines and words carried in the handicap field ("Over 5.5", "Yes",
  // "No Goal Before 35:00"); a bare line ("+1", "-0.5") stays as it is.
  return /[a-z]/i.test(s) ? translateOddText(s, t) : s;
}

/** Labels on odds (name, header) from the API; team names stay from the feed. */
export function translateOddText(raw: string, t: TFunction): string {
  const s = raw.trim();
  if (!s) return '';

  const direct = SIMPLE_ODD_KEYS[s];
  if (direct) {
    return t(direct, { defaultValue: s });
  }

  // "2 or More", "2 or 3 goals" are counts, not two teams.
  const parts = /^\d+\s+or\s+(more|\d+)/i.test(s) ? [] : s.split(/\s+or\s+/i);
  if (parts.length === 2) {
    const [a, b] = parts.map((p) => p.trim());
    if (b.toLowerCase() === 'draw') {
      return t('bet_pattern_team_or_draw', { team: a, defaultValue: s });
    }
    if (a.toLowerCase() === 'draw') {
      return t('bet_pattern_draw_or_team', { team: b, defaultValue: s });
    }
    if (b.toLowerCase() === 'tie') {
      return t('bet_pattern_team_or_tie', { team: a, defaultValue: s });
    }
    if (a.toLowerCase() === 'tie') {
      return t('bet_pattern_tie_or_team', { team: b, defaultValue: s });
    }
    return t('bet_pattern_team_or_team', {
      team1: a,
      team2: b,
      defaultValue: s,
    });
  }

  if (/^\d+(\.\d+)?$/.test(s)) return s;
  if (/^\d+-\d+$/.test(s)) return s;

  return translateSelectionByText(s) ?? s;
}

/** A clock time on its own ("80:00"): the handicap of "Goal Before" picks. */
const CLOCK_TIME = /^\d{1,3}:\d{2}$/;

export function formatTranslatedOddLabelParts(
  odd: Odd | LiveEventOdd,
  t: TFunction
): { name: string; handicap: string; header: string; extra: string } {
  const nameRaw = odd.name != null ? String(odd.name).trim() : '';
  const hc = odd.handicap;
  const handicapRaw =
    hc != null && String(hc).trim() !== '' ? String(hc).trim() : '';
  // Live headers carry the line as well (HD "O 105.5", " +1.5").
  const headerRaw = odd.header != null ? String(odd.header).trim() : '';
  // Live "Time of 2nd Goal" splits its pick into "No Goal Before" + "80:00";
  // translated apart it read "No Goal Before 80:00". Translate them as one
  // label so the time lands where each language puts it.
  if (nameRaw && CLOCK_TIME.test(handicapRaw)) {
    const joined = translateSelectionByText(`${nameRaw} ${handicapRaw}`);
    if (joined) {
      const header =
        headerRaw !== '' && headerRaw !== handicapRaw
          ? translateHandicapLine(headerRaw, t)
          : '';
      return { name: joined, handicap: '', header, extra: '' };
    }
  }
  const name = nameRaw ? translateOddText(nameRaw, t) : '';
  const handicap = handicapRaw ? translateHandicapLine(handicapRaw, t) : '';
  // Live handicap lines carry the line twice (HA "+1.5", HD " +1.5"): the
  // pick read "Team +1.5 (+1.5)".
  const header =
    headerRaw !== '' && headerRaw !== handicapRaw
      ? translateHandicapLine(headerRaw, t)
      : '';
  // The live feed's own "name" (name3) mostly repeats the handicap ("10th
  // Corner", "Last", "Match"): shown again it read "Last (Last)", untranslated.
  // Only a different word (a team, "Goals" on Next 10 Minutes) is kept.
  const extraRaw =
    'name3' in odd && odd.name3 != null ? String(odd.name3).trim() : '';
  const extra =
    extraRaw &&
    extraRaw !== handicapRaw &&
    extraRaw !== headerRaw &&
    extraRaw !== nameRaw
      ? translateHandicapLine(extraRaw, t)
      : '';
  return { name, handicap, header, extra };
}
